import React from "react";
import PropTypes from "prop-types";
import { ADMIN_FORM, ADMIN_PAYMENT_METHOD } from "./admin-payment-form-proptypes";

const propTypes = {
  paymentMethod: ADMIN_PAYMENT_METHOD.isRequired,
  adminForm: ADMIN_FORM.isRequired,
  onFieldChange: PropTypes.func.isRequired,
  formErrors: PropTypes.shape({})
};

const AdminPaymentForm = ({ paymentMethod, adminForm, onFieldChange, formErrors = {} }) => {
  const showCustomAmount = ["enterprise", "stripe_invoice"].includes(adminForm.adminPayment);
  const showBillingGroup = ["stripe_bundle", "enterprise", "stripe_invoice"].includes(
    adminForm.adminPayment
  );
  const showRenewWithCard = adminForm.adminPayment !== "stripe_bundle";
  const showBillingGroupDisclaimer = adminForm.adminPayment === "stripe_invoice";
  const billingGroupDisclaimerAccount = "partner's default Stripe customer";
  const billingGroupOptions = ["stripe_invoice", "stripe_bundle"].includes(adminForm.adminPayment)
    ? paymentMethod.linkedBillingGroupOptions
    : paymentMethod.billingGroupOptions;

  return (
    <div className="d-flex flex-column admin-payment-method pl-3 mb-3">
      <div className="d-flex flex-column form-group mt-3">
        <label htmlFor="admin_payment">Select Funding Source:</label>
        <select
          id="admin_payment"
          aria-label="Admin Payment Select"
          className="form-control admin-payment-method__input-field"
          onChange={event => onFieldChange("adminPayment", event.target.value)}
          value={adminForm.adminPayment}
        >
          {paymentMethod.fundingSourceOptions.map(paymentOption => (
            <option value={paymentOption.value} key={paymentOption.value}>
              {paymentOption.label}
            </option>
          ))}
        </select>
        <div className="text-danger">
          {formErrors.fundingSource &&
            "A valid campaign account is needed to checkout with the selected funding source"}
        </div>
      </div>
      {showCustomAmount && (
        <div className="d-flex flex-column form-group">
          <label htmlFor="admin_custom_amount">Custom amount:</label>
          <input
            type="number"
            name="admin_custom_amount"
            id="admin_custom_amount"
            step="0.01"
            min="0.01"
            className="form-control admin-payment-method__input-field"
            value={adminForm.adminCustomAmount}
            onChange={event => onFieldChange("adminCustomAmount", event.target.value)}
          />
          <div className="text-danger">
            {formErrors.customAmount && "Custom amount must be empty or greater than 0"}
          </div>
          <small className="form-text mt-1 mr-2">
            <span className="font-weight-bold">OPTIONAL:</span> Override the default campaign cost
          </small>
        </div>
      )}
      {showBillingGroup && (
        <div className="d-flex flex-column form-group">
          <label htmlFor="admin_billing_group_id">Billing Group:</label>
          <select
            className="form-control admin-payment-method__input-field"
            name="admin_billing_group_id"
            id="admin_billing_group_id"
            value={adminForm.adminBillingGroupId}
            onChange={event => onFieldChange("adminBillingGroupId", event.target.value)}
          >
            <option value="" label="" />
            <optgroup label={billingGroupOptions?.partner?.name}>
              {billingGroupOptions.partner?.options?.map(option => (
                <option value={option.value} key={option.value}>
                  {option.label} ({option.value})
                </option>
              ))}
            </optgroup>
            <optgroup label={billingGroupOptions?.other?.name}>
              {billingGroupOptions.other?.options?.map(option => (
                <option value={option.value} key={option.value}>
                  {option.label}
                </option>
              ))}
            </optgroup>
          </select>
          <div className="text-danger">
            {formErrors.billingGroupId && "Billing Group is required"}
          </div>
          {showBillingGroupDisclaimer && (
            <small className="form-text mt-1 mr-2">
              <span className="font-weight-bold">OPTIONAL:</span> If no billing group is selected,
              the charge will be applied to the {billingGroupDisclaimerAccount}.
            </small>
          )}
        </div>
      )}
      <div className="d-flex flex-column form-group">
        <div className="d-flex mb-2">
          <input
            type="radio"
            name="admin_renewal_option"
            id="renew_with_selected_funding"
            value="renew_with_selected_funding"
            className="mr-2"
            checked={adminForm.adminRenewalOption === "renew_with_selected_funding"}
            onChange={event => onFieldChange("adminRenewalOption", event.target.value)}
          />
          <label className="mb-auto" htmlFor="renew_with_selected_funding">
            Renew with selected funding
          </label>
        </div>

        <div className="d-flex flex-column flex-md-row mb-2 mb-md-0">
          <div className="d-flex mb-2 mb-md-0">
            <input
              type="radio"
              name="admin_renewal_option"
              id="do_not_renew"
              value="do_not_renew"
              className="mr-2"
              checked={adminForm.adminRenewalOption === "do_not_renew"}
              onChange={event => onFieldChange("adminRenewalOption", event.target.value)}
            />
            <label className="mr-4 mb-auto" htmlFor="do_not_renew">
              Do not renew
            </label>
          </div>

          {showRenewWithCard && (
            <div className="d-flex">
              <input
                type="radio"
                name="admin_renewal_option"
                id="with_card_on_file"
                value="with_card_on_file"
                className="mr-2"
                checked={adminForm.adminRenewalOption === "with_card_on_file"}
                onChange={event => onFieldChange("adminRenewalOption", event.target.value)}
              />
              <label className="mb-auto" htmlFor="with_card_on_file">
                Renew with card on file
              </label>
            </div>
          )}
        </div>

        <div className="d-flex mt-3 align-content-center">
          <input
            type="checkbox"
            name="restrict_cancellation"
            id="restrict_cancellation"
            value="true"
            className="mr-2"
            checked={adminForm.restrictCancellation}
            onChange={event => onFieldChange("restrictCancellation", event.target.checked)}
          />
          <label htmlFor="restrict_cancellation" className="mb-auto">
            Campaign can only be canceled by Adwerx Admin
          </label>
        </div>
      </div>
    </div>
  );
};

AdminPaymentForm.propTypes = propTypes;

export default AdminPaymentForm;
